import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';
import {
  AssetFileDropzone,
  SelectedUploadFiles,
  mergeFiles,
  splitBySize,
  usePageFileDrop,
} from '../components/AssetFileDropzone.jsx';
import { AssetLibraryList } from '../components/AssetLibraryList.jsx';
import { AssetLibraryAiSearch } from '../components/AssetLibraryAiSearch.jsx';
import { FormInput, SearchForm } from '../components/FormControls.jsx';
import { Modal } from '../components/Modal.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { clientLabel } from '../utils/clientLabel.js';

const REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'fulfilled'];

const ASSET_TABS = ['library', 'collections', 'requests', 'approvals'];

export function AdminAssetsPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ requests: [], pending_approvals: [] });
  const [library, setLibrary] = useState([]);
  const [collections, setCollections] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return ASSET_TABS.includes(t) ? t : 'library';
  });
  const [busyId, setBusyId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collectionForm, setCollectionForm] = useState({ client_id: '', title: '', project_id: '' });
  const [uploadForm, setUploadForm] = useState({ client_id: '', project_id: '', collection_slug: '', label: '' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showCollection, setShowCollection] = useState(false);

  const loadHub = () => apiJson('/api/admin/assets').then(setData);

  const loadLibrary = (q = search) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    const suffix = params.toString() ? `?${params}` : '';
    return apiJson(`/api/admin/assets/library${suffix}`).then(setLibrary);
  };

  const load = () =>
    Promise.all([
      loadHub(),
      loadLibrary(),
      apiJson('/api/admin/assets/collections').catch(() => []),
      apiJson('/api/admin/clients').catch(() => []),
      apiJson('/api/admin/projects').catch(() => []),
    ])
      .then(([, , cols, cls, projs]) => {
        setCollections(cols);
        setClients(cls);
        setProjects(projs);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'upload') {
      setShowUpload(true);
      setTab('library');
      return;
    }
    if (t && ASSET_TABS.includes(t)) {
      setTab(t);
    }
  }, [searchParams]);

  const closeUpload = () => {
    setShowUpload(false);
    setUploadFiles([]);
    setUploadForm({ client_id: '', project_id: '', collection_slug: '', label: '' });
  };

  const acceptDroppedFiles = (incoming) => {
    if (busy) return;
    const { accepted, tooLarge } = splitBySize(incoming);
    if (tooLarge.length) {
      setError(
        `${tooLarge.map((file) => file.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} larger than 20 MB.`,
      );
    } else {
      setError('');
    }
    if (!accepted.length) return;
    setUploadFiles((current) => mergeFiles(current, accepted));
    if (clients.length === 1) {
      setUploadForm((form) => (form.client_id ? form : { ...form, client_id: String(clients[0].id) }));
    }
    setShowUpload(true);
  };

  const { active: fileDragActive, rootRef: fileDropRef } = usePageFileDrop(acceptDroppedFiles);

  const closeCollection = () => {
    setShowCollection(false);
    setCollectionForm({ client_id: '', title: '', project_id: '' });
  };

  const submitCollection = async (e) => {
    e.preventDefault();
    if (!collectionForm.client_id || !collectionForm.title.trim()) return;
    setBusyId('collection');
    setError('');
    try {
      await apiJson('/api/admin/assets/collections', {
        method: 'POST',
        body: {
          client_id: Number(collectionForm.client_id),
          title: collectionForm.title.trim(),
          project_id: collectionForm.project_id ? Number(collectionForm.project_id) : null,
        },
      });
      closeCollection();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const updateRequest = async (id, payload) => {
    setBusyId(`req-${id}`);
    setError('');
    try {
      await apiJson(`/api/admin/assets/requests/${id}`, { method: 'PUT', body: payload });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const setApproval = async (blockId, approval_status, approval_note = null) => {
    setBusyId(`block-${blockId}`);
    setError('');
    try {
      await apiJson(`/api/admin/assets/blocks/${blockId}/approval`, {
        method: 'PUT',
        body: { approval_status, approval_note },
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const requests = data.requests ?? [];
  const pending = data.pending_approvals ?? [];
  const clientProjects = uploadForm.client_id
    ? projects.filter((p) => String(p.client_id) === uploadForm.client_id)
    : [];
  const clientCollections = uploadForm.client_id
    ? collections.filter((c) => String(c.client_id) === uploadForm.client_id)
    : [];

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length || !uploadForm.client_id) return;
    setBusy(true);
    setError('');
    const failures = [];
    const failedFiles = [];
    try {
      for (const file of uploadFiles) {
        const body = new FormData();
        body.append('file', file);
        body.append('client_id', uploadForm.client_id);
        if (uploadForm.project_id) body.append('project_id', uploadForm.project_id);
        if (uploadForm.collection_slug) body.append('collection_slug', uploadForm.collection_slug);
        if (uploadFiles.length === 1 && uploadForm.label.trim()) body.append('label', uploadForm.label.trim());
        try {
          await apiJson('/api/admin/assets/upload', { method: 'POST', body });
        } catch (err) {
          failures.push(`${file.name}: ${err.message}`);
          failedFiles.push(file);
        }
      }
      if (failedFiles.length) {
        setUploadFiles(failedFiles);
        setError(failures.join(' '));
      } else {
        closeUpload();
      }
      setTab('library');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={fileDropRef}>
      {fileDragActive && !showUpload ? (
        <div className="asset-drop-overlay" role="status">
          <div className="asset-drop-overlay-card">
            <strong>Drop files to upload</strong>
            <span className="muted small">They will be added to the library after you choose a client.</span>
          </div>
        </div>
      ) : null}
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          Media & assets
        </h2>
        <button type="button" className="btn-primary" onClick={() => setShowUpload(true)}>
          Upload file
        </button>
      </div>
      <p className="muted small">Asset library, client requests, and file approvals.</p>

      <div className="row-actions" style={{ marginBottom: '1rem', flexWrap: 'wrap' }} data-tour="assets-tabs">
        <button
          type="button"
          className={tab === 'library' ? 'btn-secondary' : 'btn-link'}
          onClick={() => setTab('library')}
        >
          Library ({library.length})
        </button>
        <button
          type="button"
          className={tab === 'collections' ? 'btn-secondary' : 'btn-link'}
          onClick={() => setTab('collections')}
        >
          Collections ({collections.length})
        </button>
        <button
          type="button"
          className={tab === 'requests' ? 'btn-secondary' : 'btn-link'}
          onClick={() => setTab('requests')}
        >
          Requests ({requests.filter((r) => r.status === 'pending').length} pending)
        </button>
        <button
          type="button"
          className={tab === 'approvals' ? 'btn-secondary' : 'btn-link'}
          onClick={() => setTab('approvals')}
        >
          Pending approvals ({pending.length})
        </button>
      </div>

      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}

      {tab === 'library' && !loading ? (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <AssetFileDropzone
              active={fileDragActive && !showUpload}
              busy={busy}
              onBrowse={acceptDroppedFiles}
              hint="or click to browse. Choose a client in the next step."
            />
          </div>
          {caps.ai_asset_search ? (
            <AssetLibraryAiSearch endpoint="/api/admin/assets/library/ai-search" showMeta />
          ) : null}
          <SearchForm
            onSubmit={(e) => {
              e.preventDefault();
              setLoading(true);
              void loadLibrary().finally(() => setLoading(false));
            }}
          >
            <FormInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
            />
            <button type="submit" className="btn-secondary">
              Search
            </button>
          </SearchForm>
          <AssetLibraryList items={library} showMeta />
        </>
      ) : null}

      <Modal
        open={showUpload}
        title="Upload center"
        description="Upload deliverables for a client. Files are approved immediately and appear in the library."
        onClose={closeUpload}
      >
        <form className="stack" onSubmit={(e) => void submitUpload(e)}>
          {error ? <p className="err small">{error}</p> : null}
          <AssetFileDropzone
            active={fileDragActive}
            busy={busy}
            onBrowse={acceptDroppedFiles}
            hint="or click to browse"
          />
          <SelectedUploadFiles
            files={uploadFiles}
            disabled={busy}
            onRemove={(file) =>
              setUploadFiles((current) =>
                current.filter(
                  (item) =>
                    !(item.name === file.name && item.size === file.size && item.lastModified === file.lastModified),
                ),
              )
            }
          />
          <label className="field">
            <span>Client *</span>
            <SearchableSelect
              value={uploadForm.client_id}
              onChange={(e) =>
                setUploadForm({ client_id: e.target.value, project_id: '', collection_slug: '', label: '' })
              }
              required
            >
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {clientLabel(c)}
                </option>
              ))}
            </SearchableSelect>
          </label>
          {uploadFiles.length <= 1 ? (
            <label className="field">
              <span>Label</span>
              <input
                value={uploadForm.label}
                onChange={(e) => setUploadForm((f) => ({ ...f, label: e.target.value }))}
              />
            </label>
          ) : null}
          {clientProjects.length ? (
            <label className="field">
              <span>Project (optional)</span>
              <SearchableSelect
                value={uploadForm.project_id}
                onChange={(e) => setUploadForm((f) => ({ ...f, project_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {clientProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          {clientCollections.length ? (
            <label className="field">
              <span>Collection (optional)</span>
              <SearchableSelect
                value={uploadForm.collection_slug}
                onChange={(e) => setUploadForm((f) => ({ ...f, collection_slug: e.target.value }))}
              >
                <option value="">Auto-route</option>
                {clientCollections.map((c) => (
                  <option key={c.id} value={c.collection_slug || ''}>
                    {c.title}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeUpload} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !uploadFiles.length || !uploadForm.client_id}>
              {busy ? 'Uploading…' : uploadFiles.length > 1 ? `Upload ${uploadFiles.length} files` : 'Upload file'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showCollection} title="New collection" onClose={closeCollection}>
        <form className="stack" onSubmit={(e) => void submitCollection(e)}>
          <label className="field">
            <span>Client</span>
            <SearchableSelect
              value={collectionForm.client_id}
              onChange={(e) => setCollectionForm((f) => ({ ...f, client_id: e.target.value }))}
              required
            >
              <option value="">Client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {clientLabel(c)}
                </option>
              ))}
            </SearchableSelect>
          </label>
          <label className="field">
            <span>Collection title</span>
            <input
              value={collectionForm.title}
              onChange={(e) => setCollectionForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
          </label>
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeCollection} disabled={busyId === 'collection'}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busyId === 'collection'}>
              {busyId === 'collection' ? 'Creating…' : 'Create collection'}
            </button>
          </div>
        </form>
      </Modal>

      {tab === 'collections' && !loading ? (
        <div>
          <div className="row-actions" style={{ justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button type="button" className="btn-primary" onClick={() => setShowCollection(true)}>
              New collection
            </button>
          </div>
          <ul>
            {collections.map((c) => (
              <li key={c.id} className="muted small">
                {c.title} · {c.collection_slug}
                {c.client ? ` · ${clientLabel(c.client)}` : ''}
              </li>
            ))}
          </ul>
          {!collections.length ? <p className="muted">No collections yet.</p> : null}
        </div>
      ) : null}

      {tab === 'requests' && !loading ? (
        <ul className="list-plain">
          {requests.map((r) => (
            <li key={r.id} className="auth-card flat" style={{ marginBottom: '0.75rem' }}>
              <div>
                <strong>{r.title}</strong>
                <span className="badge badge-warn"> {r.status}</span>
                <div className="muted small">
                  <Link to={`/admin/clients/${r.client_id}`}>{clientLabel(r.client) || 'Client'}</Link>
                  {r.project?.name ? ` · ${r.project.name}` : ''}
                  {r.user?.name ? ` · ${r.user.name}` : ''}
                </div>
                {r.description ? <p className="muted small">{r.description}</p> : null}
                <div className="row-actions" style={{ marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {REQUEST_STATUSES.filter((s) => s !== r.status).map((status) => (
                    <button
                      key={status}
                      type="button"
                      className="btn-link small"
                      disabled={busyId === `req-${r.id}`}
                      onClick={() => {
                        const admin_notes =
                          status === 'rejected'
                            ? window.prompt('Optional note for the client:') ?? ''
                            : r.admin_notes;
                        void updateRequest(r.id, { status, admin_notes: admin_notes || null });
                      }}
                    >
                      Mark {status}
                    </button>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {tab === 'approvals' && !loading ? (
        <ul className="list-plain">
          {pending.map((b) => (
            <li key={b.id} className="auth-card flat" style={{ marginBottom: '0.75rem' }}>
              <div>
                <strong>{b.label || b.file_name}</strong>
                <div className="muted small">
                  <Link to={`/admin/clients/${b.client_id}`}>{b.client_name}</Link>
                  {b.project_id ? (
                    <>
                      {' · '}
                      <Link to={`/admin/projects/${b.project_id}`}>Project</Link>
                    </>
                  ) : null}
                </div>
                <div className="row-actions" style={{ marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busyId === `block-${b.id}`}
                    onClick={() => void setApproval(b.id, 'approved')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn-link btn-sm"
                    disabled={busyId === `block-${b.id}`}
                    onClick={() => {
                      const note = window.prompt('Rejection reason (optional):') ?? '';
                      void setApproval(b.id, 'rejected', note || null);
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {!loading && tab === 'requests' && !requests.length ? <p className="muted">No asset requests.</p> : null}
      {!loading && tab === 'approvals' && !pending.length ? (
        <p className="muted">No files awaiting approval.</p>
      ) : null}
    </div>
  );
}
